import { useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/feature/Toast";
import ConfirmDialog from "@/pages/admin/dashboard/components/ConfirmDialog";
import EmptyState from "@/pages/admin/dashboard/components/EmptyState";
import { timeAgo } from "@/pages/admin/dashboard/types";
import type { AdminProfile, Role } from "@/pages/admin/dashboard/types";

interface UsersTabProps {
  profiles: AdminProfile[];
  currentUserId?: string;
  onRefresh: () => void;
}

const ROLE_META: Record<Role, { label: string; badge: string }> = {
  user: { label: "Usuario", badge: "bg-background-100 text-foreground-600" },
  admin: { label: "Administrador", badge: "bg-accent-100 text-accent-800" },
  super_admin: { label: "Super Admin", badge: "bg-primary-100 text-primary-700" },
};

function initials(name: string | null, email: string | null): string {
  return (name ?? email ?? "U").charAt(0).toUpperCase();
}

export default function UsersTab({
  profiles,
  currentUserId,
  onRefresh,
}: UsersTabProps) {
  const { showToast } = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [blockTarget, setBlockTarget] = useState<AdminProfile | null>(null);

  const sorted = useMemo(
    () =>
      [...profiles].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
    [profiles]
  );

  const changeRole = async (target: AdminProfile, role: Role) => {
    if (role === "super_admin") {
      showToast(
        "No puedes crear otro super admin desde aquí. Usa la transferencia explícita.",
        "info"
      );
      return;
    }
    if (target.role === role) return;
    setBusyId(target.id);
    const { error } = await supabase.rpc("change_user_role", {
      target_uuid: target.id,
      new_role: role,
    });
    setBusyId(null);
    if (error) {
      showToast(error.message || "No se pudo actualizar el rol.", "error");
      return;
    }
    showToast(`Rol de ${target.full_name ?? "usuario"} actualizado.`, "success");
    onRefresh();
  };

  const confirmBlock = async () => {
    if (!blockTarget) return;
    const isBlocked = !blockTarget.is_active;
    setBusyId(blockTarget.id);
    const { error } = await supabase.rpc(
      isBlocked ? "unblock_user" : "block_user",
      {
        target_uuid: blockTarget.id,
        ...(isBlocked ? {} : { reason: "Suspensión administrativa" }),
      }
    );
    setBusyId(null);
    setBlockTarget(null);
    if (error) {
      showToast(error.message || "No se pudo actualizar la cuenta.", "error");
      return;
    }
    showToast(isBlocked ? "Cuenta reactivada." : "Cuenta suspendida.", "success");
    onRefresh();
  };

  if (profiles.length === 0) {
    return (
      <EmptyState
        icon="ri-team-line"
        title="Sin usuarios"
        description="Aún no hay usuarios registrados en la plataforma."
      />
    );
  }

  const isBlockedTarget = !blockTarget?.is_active;

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[820px] text-left">
        <thead>
          <tr className="border-b border-background-200/70 bg-background-100/60 text-xs font-semibold uppercase tracking-wide text-foreground-500">
            <th className="px-5 py-3">Usuario</th>
            <th className="px-5 py-3">Tipo</th>
            <th className="px-5 py-3">Rol</th>
            <th className="px-5 py-3">Estado</th>
            <th className="px-5 py-3">Registro</th>
            <th className="px-5 py-3 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((p) => {
            const meta = ROLE_META[p.role];
            const isSelf = p.id === currentUserId;
            const isSuper = p.role === "super_admin";
            return (
              <tr
                key={p.id}
                className="border-b border-background-200/60 transition-colors last:border-0 hover:bg-background-100/40"
              >
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white">
                      {initials(p.full_name, p.email)}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-foreground-950">
                        {p.full_name ?? "Usuario"}
                        {isSelf && (
                          <span className="ml-1.5 text-xs font-normal text-foreground-400">
                            (tú)
                          </span>
                        )}
                      </p>
                      <p className="truncate text-xs text-foreground-500">{p.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 text-sm text-foreground-700">
                  {p.account_type === "seller" ? "Vendedor" : "Comprador"}
                </td>
                <td className="px-5 py-3">
                  <span
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${meta.badge}`}
                  >
                    {meta.label}
                  </span>
                </td>
                <td className="px-5 py-3">
                  {!p.is_active ? (
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-800">
                      <span className="h-1.5 w-1.5 rounded-full bg-accent-500"></span>
                      Suspendido
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-secondary-500"></span>
                      Activo
                    </span>
                  )}
                </td>
                <td className="px-5 py-3 text-sm text-foreground-600">
                  {timeAgo(p.created_at)}
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {!isSuper && (
                      <select
                        value={p.role}
                        disabled={isSelf || busyId === p.id}
                        onChange={(e) => changeRole(p, e.target.value as Role)}
                        className="cursor-pointer rounded-md border border-background-200 bg-background-50 px-2 py-1.5 text-xs text-foreground-900 outline-none transition-colors focus:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <option value="user">Usuario</option>
                        <option value="admin">Administrador</option>
                      </select>
                    )}
                    {!isSuper && !isSelf && (
                      <button
                        type="button"
                        disabled={busyId === p.id}
                        onClick={() => setBlockTarget(p)}
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-200 px-3 py-1.5 text-xs font-semibold text-accent-700 transition-colors hover:bg-accent-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <i
                          className={`${
                            !p.is_active
                              ? "ri-user-received-line"
                              : "ri-user-forbid-line"
                          } text-sm`}
                        ></i>
                        {!p.is_active ? "Reactivar" : "Suspender"}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <ConfirmDialog
        open={blockTarget !== null}
        title={isBlockedTarget ? "¿Reactivar esta cuenta?" : "¿Suspender esta cuenta?"}
        description={
          isBlockedTarget
            ? `${blockTarget?.full_name ?? "El usuario"} podrá volver a iniciar sesión.`
            : `${blockTarget?.full_name ?? "El usuario"} dejará de poder iniciar sesión.`
        }
        confirmLabel={isBlockedTarget ? "Reactivar" : "Suspender"}
        tone={isBlockedTarget ? "primary" : "danger"}
        loading={busyId === blockTarget?.id}
        onConfirm={confirmBlock}
        onCancel={() => setBlockTarget(null)}
      />
    </div>
  );
}